import { CommonModule } from "@angular/common";
import {
  Component,
  inject,
  OnInit
} from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { MessageService } from "primeng/api";
import { filter, Observable, switchMap } from "rxjs";
import { AddNewAppModalComponent } from "../../../components/modal-templates/add-new-app-modal/add-new-app-modal.component";
import { DeleteCautionModalComponent } from "../../../components/modal-templates/delete-caution-modal/delete-caution-modal.component";
import { DeleteConfirmationComponent } from "../../../components/modal-templates/delete-confirmation/delete-confirmation.component";
import { GenericFormModalComponent } from "../../../components/modal-templates/generic-form-modal/generic-form-modal.component";
import { SharedImports } from "../../../components/shared.module";
import { AutomationActions } from "../../../core/ngrx/actions/automation.actions";
import {
  selectApplications,
  selectAutomationError,
  selectLoading,
} from "../../../core/ngrx/selectors/automation.selectors";
import { applicationDeleteTableColumns } from "../../../core/table-templates/application-delete-caution-table";
import { applicationOverviewTableColumns } from "../../../core/table-templates/automation-application-table";
import { MessageEnum } from "../../../core/utils/message.enum";
import { BaseLayoutComponent } from "../../base-layout.component";

@Component({
  selector: "app-application-overview",
  standalone: true,
  imports: [...SharedImports, CommonModule],
  templateUrl: "./application-overview.component.html",
  styleUrl: "./application-overview.component.scss",
})
export class ApplicationOverviewComponent
  extends BaseLayoutComponent
  implements OnInit
{
  columns = applicationOverviewTableColumns;
  options = {
    isExpandable: false
  };
  dataSource: any;
  applications$!: Observable<any>;
  loading$!: Observable<boolean>;
  error$!: Observable<string | null>;
  selectedApplications: any = [];
  iconSrcDelete: string =
    "../../../../../assets/images/virtusa-icons/remove-icon-dark.svg";
  private modalService = inject(NgbModal);
  automationProjectID: string = "";
  projectID: string = "";
  isLoading: boolean = false;
  addApplicationFields = [
    {
      name: "Application Name",
      type: "text-input",
      key: "name",
      id: "name",
      placeholder: "Application Name",
      wrapperClass: "col-md-6",
      validations: {
        required: true,
      },
    },
    {
      name: "Application Type",
      type: "select",
      key: "paradigm",
      id: "paradigm",
      placeholder: "Application Type",
      wrapperClass: "col-md-6",
      options: [
        {
          label: "Web",
          value: "Web",
        },
        {
          label: "Mobile",
          value: "Mobile",
        },
      ],
      validations: {
        required: true,
      },
    },
  ];

  private messageService = inject(MessageService);

  ngOnInit(): void {
    this.initializeApplicationData();
  }

  initializeApplicationData() {
    this.applications$ = this.projectService.getAutomationProjectId().pipe(
      filter((id: any) => !!id), // Ensures id is not null/undefined/empty
      switchMap((id: string) => this.store.select(selectApplications(id)))
    );

    this.projectService.getAutomationProjectId().subscribe((id: any) => {
      this.automationProjectID = id;
    });

    this.projectService.getProjectId().subscribe((id) => {
      if (id != null) {
        this.projectID = id;
      }
    });

    this.loading$ = this.store.select(selectLoading);
    this.error$ = this.store.select(selectAutomationError);

    this.loadNgrxData();
  }

  loadNgrxData() {
    this.applications$.subscribe((item) => {
      if (item) {
        const applicationList = Object.keys(item).map((key) => ({
          ...item[key],
          paradigmType:
            item[key]?.paradigm === "Mobile"
              ? `${item[key]?.paradigm} - (${item[key]?.mobileAppType})`
              : item[key]?.paradigm,
          displayName: item[key]?.name,
          browser: item[key]?.browser ? item[key]?.browser : "-",
          mobilePlatform: item[key]?.mobilePlatform
            ? item[key]?.mobilePlatform
            : "-",
          mobileFileName: item[key]?.mobileFileName
            ? item[key]?.mobileFileName
            : "-",
          icon: "sprint",
          order: 1,
          level: 2,
          type: "application",
          isAdd: true,
          view: "applicationsView",
          children: [],
        }));
        this.dataSource = applicationList;
      }
    });
  }

  addNewApplication() {
    this.addApplicationFields.forEach((element: any) => {
      element["value"] = "";
    });
    this.openGenericModal(false, {});
  }

  addNewApp(): void {
    this.openNewAppModal(false, null);
  }

  openNewAppModal(isEdit: boolean, row: any): void {
    const modalRef = this.modalService.open(AddNewAppModalComponent, {
      centered: true,
      size: "lg",
      windowClass: "add-item-modal",
      backdrop: "static",
    });

    if (isEdit && row) {
      modalRef.componentInstance.initialData = row;
      modalRef.componentInstance.isEdit = true;
    }

    modalRef.result.then(
      (result: any) => {
        if (!result) return;

        if (isEdit ? this.isNameExists(result.applicationName, row?.id)
                   : this.isNameExists(result.applicationName)) {
          return this.toaster.pop({
            type: "error",
            text: MessageEnum.APP_NAME_DUPLICATE,
          });
        }

        const formData = new FormData();
        formData.append("name", result.applicationName ?? "");
        formData.append("paradigm", result.applicationType ?? "Web");

        if (result.applicationType === "Mobile") {
          formData.append("mobileAppType", result.mobileAppType ?? "");
          if (result.mobilePlatform) {
            formData.append("mobilePlatform", result.mobilePlatform);
          }
          const mobileFile = result.mobileFileName;
          if (mobileFile instanceof File) {
            formData.append("file", mobileFile);
          }
        } else {
          // Web
          formData.append("browser", result.browser ?? "");
          formData.append("applicationUrl", result.baseUrl ?? "");
          formData.append(
            "credentials",
            new Blob([JSON.stringify(result.credentials ?? [])], { type: "application/json" })
          );
        }

        if (isEdit) {
          this.store.dispatch(
            AutomationActions.updateApplication({
              projectId: this.projectID,
              automationProjectId: this.automationProjectID,
              id: row?.id,
              changes: formData,
            })
          );
        } else {
          this.store.dispatch(
            AutomationActions.addApplication({
              projectId: this.projectID,
              automationProjectId: this.automationProjectID,
              application: formData,
            })
          );
        }
      },
      () => { /* dismissed */ }
    );
  }

  removeApplication() {
    this.isLoading = true;

    let idList = this.selectedApplications.map((item: any) => item.id);
    this.restService
      .deleteValidationApp(this.projectID, this.automationProjectID, idList)
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          let rowData = response?.data;
          rowData = this.assignLevels(rowData);
          console.log("row data with levels ->", rowData);
          const deletableIds = this.selectedApplications
            .filter((item: any) => !rowData?.some((d: any) => d.id === item.id))
            .map((item: any) => item.id);

          console.log("deletable ids ->", deletableIds);
          const data = {
            deleteList: deletableIds,
            skippedList: rowData,
          };

          if (
            Array.isArray(data?.skippedList) &&
            data?.skippedList?.length > 0
          ) {
            this.removeApplicationCaution(data);
          } else {
            this.removeApplicationConfirmation();
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          this.toaster.pop({
            type: "error",
            text: MessageEnum.AUTOMATION_APPLICATION_DELETE_VALIDATION_FAILURE,
          });
        },
      });
  }

  assignLevels(data: any[], level: number = 0): any[] {
    return data.map((item) => {
      const hasScript = this.containsScript(item);
      const newItem = {
        ...item,
        level,
        scriptLevel: hasScript,
      };

      if (item.children && item.children.length > 0) {
        newItem.children = this.assignLevels(item.children, level + 1);
      }

      return newItem;
    });
  }

  containsScript(item: any): boolean {
    if (item.script) {
      return true;
    } else {
      return false;
    }
  }

  removeApplicationCaution(tableData: any) {
    this.isLoading = false;
    let nonDeletableCount = tableData.skippedList.length;
    let modalRef = this.modalService.open(DeleteCautionModalComponent, {
      centered: true,
      size: "lg",
      backdrop: "static",
    });
    modalRef.componentInstance.configs = {
      subTitle: "Unable to Delete All Selected Applications",
      cautionMessage1:
        nonDeletableCount +
        " of the selected applications are currently used in test scripts. To avoid breaking existing tests, these applications cannot be deleted.",
      cautionMessage2:
        "Only applications that are not currently in use will be removed.",
      footerMessage1:
        "You can delete the remaining applications that are not used in any scripts.",
      footerMessage2:
        "To delete the listed applications, first remove their references from the test scripts mentioned above.",
      tableData: tableData.skippedList,
      tableTitle: "Details of Applications in Use",
      deleteButtonText: "Delete Unused Applications Only",
      columns: applicationDeleteTableColumns,
      projectId: this.projectID,
      automationProjectId: this.automationProjectID,
    };
    modalRef.result.then((result) => {
      if (result) {
        //delete applications / bulk

        this.store.dispatch(
          AutomationActions.deleteApplications({
            projectId: this.projectID,
            automationProjectId: this.automationProjectID,
            idList: tableData.deleteList,
          })
        );
      }
    });
  }

  removeApplicationConfirmation() {
    this.isLoading = false;
    let modalRef = this.modalService.open(DeleteConfirmationComponent, {
      windowClass:"delete-confirmation-window",
      centered: true,
      size: "sm",
      backdrop: "static",
    });
    modalRef.componentInstance.data = {
      title: "Confirm Deletion",
      description: `Are you sure you want to delete Applications`,
    };
    modalRef.result.then((result) => {
      if (result) {
        //delete applications / bulk

        this.store.dispatch(
          AutomationActions.deleteApplications({
            projectId: this.projectID,
            automationProjectId: this.automationProjectID,
            idList: this.selectedApplications.map((item: any) => item.id),
          })
        );
      }
    });
  }

  changeRemoveIcon(isActive: boolean) {
    this.iconSrcDelete = isActive
      ? "../../../../../assets/images/virtusa-icons/remove-icon-light.svg"
      : "../../../../../assets/images/virtusa-icons/remove-icon-dark.svg";
  }

  handleRowSelect(event: any) {
    this.selectedApplications = event?.data;
  }

  handleActionsInTable(event: any) {
    if (event.type == "svg") {
      this.openNewAppModal(true, event.data?.row);
    }
  }


  openGenericModal(isEdit: boolean, row: any) {
    const modalRef = this.modalService.open(GenericFormModalComponent, {
      centered: true,
      size: "lg",
      windowClass: "add-item-modal",
      backdrop: 'static',
    });

    modalRef.componentInstance.configs = {
      title: isEdit ? "Update Application" : "Add New Application",
      isEdit,
    };

    // Prepare fields
    let editedFields = this.addApplicationFields;
    if (isEdit && row.paradigm) {
      editedFields = this.updateFieldList(
        { field: { id: "paradigm" }, value: row.paradigm },
        this.addApplicationFields
      );

      if (row.paradigm === "Mobile" && row.mobileAppType === "Mobile-Web") {
        const mobileFields = this.updateFieldList(
          { field: { id: "mobileAppType" }, value: "Mobile-Web" },
          editedFields
        );
        editedFields = [...mobileFields];
      }
      editedFields = editedFields.map((f: any) => ({
        ...f,
        value: row[f.id] ?? "",
      }));
    }

    modalRef.componentInstance.fields = isEdit
      ? editedFields
      : this.addApplicationFields;

    // Handle dynamic field changes
    modalRef.componentInstance.fieldChanged.subscribe((change: any) => {
      const updatedFields = this.updateFieldList(
        change,
        modalRef.componentInstance.fields
      ).map((field: any) => ({
        ...field,
        value: change?.modal[field?.id] ?? "",
      }));
      modalRef.componentInstance.fields = updatedFields;
    });

    // Handle form submission
    modalRef.result.then((result: any) => {
      const formPayload = this.getFormPayload(result);
      if (isEdit) {
        if (this.isNameExists(result?.value?.name, row?.id)) {
          return this.toaster.pop({
            type: "error",
            text: MessageEnum.APP_NAME_DUPLICATE,
          });
        }
        this.store.dispatch(
          AutomationActions.updateApplication({
            projectId: this.projectID,
            automationProjectId: this.automationProjectID,
            id: row?.id,
            changes: formPayload,
          })
        );
      } else {
        if (this.isNameExists(result?.name)) {
          return this.toaster.pop({
            type: "error",
            text: MessageEnum.APP_NAME_DUPLICATE,
          });
        }
        this.store.dispatch(
          AutomationActions.addApplication({
            projectId: this.projectID,
            automationProjectId: this.automationProjectID,
            application: formPayload,
          })
        );
      }
    });
  }

  getFormPayload(result: any): FormData {
    const formData = new FormData();

    // Required fields
    formData.append("name", result?.name ?? "");
    formData.append("paradigm", result?.paradigm ?? "");

    // Optional fields
    if (result?.browser) {
      formData.append("browser", result.browser);
    }

    // Mobile-specific fields
    if (result?.paradigm === "Mobile") {
      formData.append("mobileAppType", result?.mobileAppType ?? "");
      formData.append("mobilePlatform", result?.mobilePlatform ?? "");

      // File handling
      const mobileFile = result?.mobileFileName;
      if (mobileFile instanceof File) {
        formData.append("file", mobileFile);
      }
    }

    return formData;
  }

  refreshData() {
    this.store.dispatch(
      AutomationActions.loadApplications({
        projectId: this.projectID,
        automationProjectId: this.automationProjectID,
      })
    );
  }

  isNameExists(name: string, excludeId?: string): boolean {
    const normalizedName = name?.toLowerCase()?.trim();
    return this.dataSource
      ?.filter((app: any) => app?.id !== excludeId) // exclude current editing item
      ?.some((app: any) => app?.name?.toLowerCase()?.trim() === normalizedName);
  }

  updateFieldList(data: any, fields: any) {
    if (data?.field?.id === "paradigm" && data?.value === "Web") {
      return [
        ...this.addApplicationFields,
        {
          name: "Browser",
          type: "select",
          id: "browser",
          key: "browser",
          placeholder: "Browser",
          wrapperClass: "col-md-6",
          options: [
            {
              label: "Chrome",
              value: "Chrome",
            },
            {
              label: "Firefox",
              value: "Firefox",
            },
            {
              label: "Edge",
              value: "Edge",
            },
          ],
          validations: {
            required: true,
          },
        },
      ];
    } else if (data?.field?.id === "paradigm" && data?.value === "Mobile") {
      return [
        ...this.addApplicationFields,
        {
          name: "Type",
          type: "select",
          id: "mobileAppType",
          key: "mobileAppType",
          placeholder: "Type",
          wrapperClass: "col-md-6",
          validations: {
            required: true,
          },
          options: [
            {
              label: "Native",
              value: "Native",
            },
            {
              label: "Hybrid",
              value: "Hybrid",
            },
            {
              label: "Mobile-Web",
              value: "Mobile-Web",
            },
            {
              label: "Cross-Platform",
              value: "Cross-Platform",
            },
          ],
        },
        {
          name: "Mobile Platform",
          type: "select",
          id: "mobilePlatform",
          key: "mobilePlatform",
          placeholder: "Mobile Platform",
          wrapperClass: "col-md-6",
          options: [
            {
              label: "Android",
              value: "ANDROID",
            },
            {
              label: "iOS",
              value: "IOS",
            },
          ],
        },
        {
          name: "Upload APK",
          id: "mobileFileName",
          key: "mobileFileName",
          type: "file-upload",
          placeholder: "APK",
          wrapperClass: "col-md-12",
          configs: {
            fileTypes: ".apk, .ipa",
            buttonLabel: "Choose file",
            placeholderText: "APK and IPA files only",
          },
        },
      ];
    } else if (
      data?.field?.id === "mobileAppType" &&
      data?.value === "Mobile-Web"
    ) {
      const browserObj = {
        name: "Browser",
        type: "select",
        id: "browser",
        key: "browser",
        placeholder: "Browser",
        wrapperClass: "col-md-6",
        options: [
          {
            label: "Chrome",
            value: "Chrome",
          },
          {
            label: "Firefox",
            value: "Firefox",
          },
          {
            label: "Edge",
            value: "Edge",
          },
        ],
      };
      const browser = fields.filter((item: any) => item?.id === "browser");
      if (browser?.length === 0) {
        fields.splice(4, 0, browserObj);
      }
      return fields;
    } else if (
      data?.field?.id === "mobileAppType" &&
      data?.value !== "Mobile-Web"
    ) {
      const index = fields.findIndex((item: any) => item.id === "browser");
      if (index !== -1) {
        fields.splice(index, 1);
      }
    } else {
      return fields;
    }
  }
}
